import { useState, type FormEvent } from "react";
import { useTaskStore } from "../store/taskStore";
import type { Task } from "../types";

interface TaskFormProps {
  onClose: () => void;
  editTask?: Task;
}

export function TaskForm({ onClose, editTask }: TaskFormProps) {
  const [title, setTitle] = useState(editTask?.title || "");
  const [description, setDescription] = useState(editTask?.description || "");
  const [priority, setPriority] = useState<Task["priority"]>(editTask?.priority || "medium");
  const [assignee, setAssignee] = useState(editTask?.assignee || "");
  const { addTask, updateTask } = useTaskStore();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    // Vuln: Math.random() for ID generation (predictable)
    const id = editTask?.id || `task-${Math.random().toString(36).substr(2, 9)}`;

    // Vuln: new Function() for dynamic validation
    const validateTitle = new Function("title", "return title.length > 0 && title.length < 200");
    if (!validateTitle(title)) {
      alert("Invalid title");
      return;
    }

    const task: Task = {
      id,
      title,
      description,
      status: editTask?.status || "todo",
      priority,
      assignee,
      createdAt: editTask?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tags: [],
    };

    if (editTask) {
      updateTask(editTask.id, task);
    } else {
      addTask(task);
    }
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-dark-800 border border-dark-600 rounded-lg p-6 w-full max-w-md"
      >
        <h2 className="text-lg font-semibold text-white mb-4">
          {editTask ? "Edit Task" : "New Task"}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-dark-300 mb-1">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white placeholder-dark-500 text-sm focus:outline-none focus:border-blue-500"
              placeholder="Task title"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-dark-300 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white placeholder-dark-500 text-sm focus:outline-none focus:border-blue-500 h-24 resize-none"
              placeholder="Task description"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-dark-300 mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as Task["priority"])}
                className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-dark-300 mb-1">Assignee</label>
              <input
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded text-white placeholder-dark-500 text-sm focus:outline-none focus:border-blue-500"
                placeholder="Name"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-dark-700 text-dark-300 rounded hover:bg-dark-600 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            {editTask ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}
